import {
  createContext,
  startTransition,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';

import { getSupabase } from './supabase';

/**
 * Session context (E2.3). Holds the Supabase session so the app can branch on
 * login state and the API client can read the current access token.
 *
 * The Supabase SDK owns token storage + refresh; this
 * context just mirrors the SDK's session into React state via onAuthStateChange.
 */
interface AuthState {
  /** Current session, or null when signed out. */
  session: Session | null;
  /** Convenience flag for login-gated UI. */
  isAuthenticated: boolean;
  /** True until the initial session lookup resolves (avoids login/logout flash). */
  isLoading: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Seed from any persisted session, then subscribe to all auth changes
    // (sign-in via callback, token refresh, sign-out).
    //
    // 세션 반영은 전환(transition)이다 (docs/SSR.md hydration 규칙 2). 로그인하면 상세의 queryKey 가
    // 'anon' → 'auth' 로 바뀌어 useSuspenseQuery 가 다시 suspend 한다. 전환이 아니면 이미 보인 본문이
    // 「불러오는 중…」 으로 바뀐다. 전환이면 새 키가 올 때까지 비로그인 화면을 그대로 둔다.
    getSupabase().auth.getSession().then(({ data }) => {
      startTransition(() => {
        setSession(data.session);
        setIsLoading(false);
      });
    });

    const {
      data: { subscription },
    } = getSupabase().auth.onAuthStateChange((_event, nextSession) => {
      startTransition(() => {
        setSession(nextSession);
        setIsLoading(false);
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  // 서버는 항상 비로그인으로 그린다(docs/SSR.md 「인증」). 세션이 확인되면 서버가 넘긴 비로그인 응답
  // (isMine · isBookmarked · myReaction · hasVoted …)을 다시 받는다. 토큰 갱신처럼 로그인 상태가
  // 그대로인 변화에는 반응하지 않는다.
  const queryClient = useQueryClient();
  const authed = session !== null;
  const wasAuthed = useRef(false);
  useEffect(() => {
    if (authed && !wasAuthed.current) void queryClient.invalidateQueries();
    wasAuthed.current = authed;
  }, [authed, queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      session,
      isAuthenticated: session !== null,
      isLoading,
    }),
    [session, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

/**
 * loader 용 — 지금 세션이 있는가. 서버는 항상 비로그인이다(docs/SSR.md 「인증」).
 * 브라우저 이동의 loader 가 화면과 같은 키('anon' | 'auth')로 채우게 한다 — 다르면 한 번 더 받는다.
 */
export async function hasSession(): Promise<boolean> {
  if (import.meta.env.SSR) return false;
  const { data } = await getSupabase().auth.getSession();
  return data.session !== null;
}
